import type { ProductCardData } from "@models/product";
import { ProductCard } from "./ProductCard";
import { getPaginationWindow } from "@utils/pagination";
import { Icon, Skeleton } from "@common/index";

interface ProductGridProps {
    // The card's own shape, so the lighter popular/featured payloads fit (#401).
    products: ProductCardData[];
    pageNumber: number;
    totalPages: number;
    isLoading: boolean;
    // Omit for a single-page grid (the popular and featured pages): the
    // pagination is then never rendered.
    onPageChange?: (newPage: number) => void;
}

const SKELETON_COUNT = 8;

export const ProductGrid = ({
    products,
    pageNumber,
    totalPages,
    isLoading,
    onPageChange,
}: ProductGridProps) => {
    const isEmpty = !isLoading && products.length === 0;

    return (
        <section className="px-2 py-4">
            {isEmpty ? (
                <div className="text-center py-12 text-neutral/60">
                    No products found.
                </div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {isLoading && products.length === 0
                        ? Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                            <Skeleton
                                key={i}
                                data-testid="product-skeleton"
                                className="aspect-[3/4] rounded-lg"
                            />
                        ))
                        : products.map((p) => (
                            <ProductCard key={p.id} product={p} />
                        ))}
                </div>
            )}

            {totalPages > 1 && onPageChange && (
                <Pagination
                    pageNumber={pageNumber}
                    totalPages={totalPages}
                    onPageChange={onPageChange}
                />
            )}
        </section>
    );
};

interface PaginationProps {
    pageNumber: number;
    totalPages: number;
    onPageChange: (page: number) => void;
}

const Pagination = ({
    pageNumber,
    totalPages,
    onPageChange,
}: PaginationProps) => {
    const window = getPaginationWindow(pageNumber, totalPages);

    return (
        <nav aria-label="Pagination" className="flex justify-center mt-6">
            <div className="join border border-base-200 bg-primary/10 shadow-sm rounded-lg">
                <button
                    type="button"
                    aria-label="Previous page"
                    className="join-item btn btn-sm btn-ghost text-neutral"
                    disabled={pageNumber <= 1}
                    onClick={() => onPageChange(pageNumber - 1)}
                >
                    <Icon name="chevron_left" className="text-lg" />
                </button>
                {window.map((entry, i) =>
                    entry === "..." ? (
                        <span
                            key={`ellipsis-${i}`}
                            aria-hidden="true"
                            className="join-item btn btn-sm btn-ghost no-animation pointer-events-none text-neutral/40"
                        >
                            …
                        </span>
                    ) : (
                        <button
                            key={entry}
                            type="button"
                            aria-label={`Go to page ${entry}`}
                            aria-current={
                                entry === pageNumber ? "page" : undefined
                            }
                            className={`join-item btn btn-sm font-bold ${
                                entry === pageNumber
                                    ? "btn-secondary"
                                    : "btn-ghost text-neutral hover:text-neutral"
                            }`}
                            onClick={() => onPageChange(entry)}
                        >
                            {entry}
                        </button>
                    ),
                )}
                <button
                    type="button"
                    aria-label="Next page"
                    className="join-item btn btn-sm btn-ghost text-neutral"
                    disabled={pageNumber >= totalPages}
                    onClick={() => onPageChange(pageNumber + 1)}
                >
                    <Icon name="chevron_right" className="text-lg" />
                </button>
            </div>
        </nav>
    );
};
